import React, { memo, useMemo } from "react";
import { PRODUCT_ANIMATION_MAX_TILES } from "../constants";
import { urlDomain } from "../utils/productMode";
import { BrandBlock, OverlayShell, StatusBlock, useOverlayRun, usePreloadImages } from "./ProductOverlay";

/**
 * Product mode's intro: a full-screen takeover that plays when the view opens.
 *
 * "Extracting data…" with a counter that climbs to the table's total while the
 * products' images land one after another on a wall below — the data is
 * already here, the intro just lets a recording show it arriving. The last
 * stretch flips to "Extraction complete", then the overlay fades and the view
 * underneath replays its reveal.
 *
 * @param {string[]} images One image per row, in row order
 * @param {number} total Products in the table (the counter's end value)
 * @param {{ name?: string, url?: string, favicon?: string }|null} source
 * @param {number} durationMs Full run, from mount to the start of the fade
 * @param {() => void} onLeave Fired as the fade-out starts
 * @param {() => void} onDone Fired when the overlay can unmount
 */

const TileWall = memo(({ tiles, stepMs }) => (
  <div className="relative flex-1 min-h-0 w-full max-w-md px-4 mt-6 overflow-hidden">
    {/* The scan band only sweeps the tiles, not the empty space under them. */}
    <div className="relative">
      <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(6, minmax(0, 1fr))" }}>
        {tiles.map((url, index) => (
          <div
            key={`${url}-${index}`}
            className="pm-intro-tile relative aspect-square rounded-md overflow-hidden bg-white/[0.06] ring-1 ring-white/10"
            style={{ animationDelay: `${Math.round(index * stepMs)}ms` }}
          >
            <img
              src={url}
              alt=""
              referrerPolicy="no-referrer"
              draggable={false}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.visibility = "hidden";
              }}
            />
          </div>
        ))}
      </div>
      <div aria-hidden="true" className="pm-intro-scan pointer-events-none absolute inset-0" />
    </div>
  </div>
));
TileWall.displayName = "TileWall";

const ProductIntro = ({ images, total, source, durationMs, onLeave, onDone }) => {
  const tiles = useMemo(() => (images || []).slice(0, PRODUCT_ANIMATION_MAX_TILES), [images]);
  const productCount = Math.max(0, Number(total) || 0);
  const { phase, isComplete, count, landingMs, leave } = useOverlayRun({ durationMs, total: productCount, onLeave, onDone });
  const stepMs = tiles.length > 1 ? landingMs / tiles.length : landingMs;
  usePreloadImages(tiles);

  const domain = urlDomain(source?.url);

  return (
    <OverlayShell phase={phase} onSkip={leave} ariaLabel="Extracting data">
      <BrandBlock />
      <StatusBlock
        complete={isComplete}
        headline="Extracting data"
        completeHeadline="Extraction complete"
        subline={domain ? `from ${domain}` : null}
        favicon={source?.favicon}
        landingMs={landingMs}
        countLine={
          <>
            <span className="text-4xl font-bold tabular-nums">{(isComplete ? productCount : count).toLocaleString()}</span>
            <span className="text-sm text-gray-500">{productCount === 1 ? "product" : "products"}</span>
          </>
        }
      />
      <TileWall tiles={tiles} stepMs={stepMs} />
    </OverlayShell>
  );
};

export default ProductIntro;
